// mobile-panel-chrome.ts — narrow-viewport side-panel chrome (template.js cleanup roadmap T2.3, 2026-06-14).
//
// Byte-faithful port of the mobile/cramped-viewport panel-tab system: viewport classification
// (zoom-aware nominal width), the html.ft-mobile-panel-chrome / ft-sidelog-open / ft-sideinfo-open
// sync, and the edge-tab attention/open-state helpers — carved out of template.js.
//
// __ftShouldQuietRefreshSideInfoPanel is INCLUDED here (the roadmap listed it under T2.3's neighbours
// implicitly): it is literally `return isNarrowViewport()`, so co-locating it avoids a cross-bundle
// guarded call. It's already treated as an ambient global by ft3d-move-handlers / encounter-overlays /
// pickpocket / side-panels-init, all of which call it at runtime.
//
// Public surface (window-attached): every original top-level fn here was a window global; consumers
// span the encounter-map bundle (DragManager / XZScrollPin — loaded BEFORE template.js but
// calling at runtime), side-panels-init.js (open/close + attention), encounter-overlays, pickpocket,
// and template.js (resize / DOMContentLoaded handlers). The 4 FT_PANEL_* consts stay module-local.
//
// Loaded AFTER template.js; deps reached at runtime — isSideLogOpen / isSideInfoPanelOpen /
// syncMobileLogTabSidelogWarningHighlight (side-panels-init), window.ftSyncEncMobileTab (guarded).

// =============================================================================
// MOBILE PANEL TABS
//
// On "narrow-ish" viewports for *side panels* we do NOT force the drawers open on
// ambient events — edge tabs + attention pulse instead.
//
// **Do NOT** drive this from bare `matchMedia('(max-width: …)')` alone: desktop page
// zoom shrinks CSS layout width, so breakpoints “fire early” (~101%) even on large
// monitors. We classify “small-screen / tablet” using an ~unzoomed nominal width:
// layout CSS width × visualViewport.scale (when available).
//
// “Cramped desktop” tabs: compare *layout width after zoom* to a fraction of zoom-
// invariant *nominal* width (~innerWidth × visualViewport.scale). That aligns Chrome /
// Firefox: both fire near the same intrinsic zoom (~133%).
//
// The tabs themselves live in includes/template/template_page_footer.php.
// =============================================================================

/** Bootstrap `xxl` nominal width (~unzoomed) — tablets / laptops below this band use panel-tab UX regardless of pinch/ctrl-zoom. */
const FT_PANEL_CHROME_MEDIA_MIN_XXL_PX = 1400;

/**
 * Ratio of nominal : layout widths at which we enable panel-tab / quiet-refresh mode
 * (~133% intrinsic browser zoom → layout ≈ nominal / RATIO). Tunable independently
 * from literal menu % labels across Chrome vs Firefox.
 */
const FT_PANEL_ZOOM_LAYOUT_TO_NOMINAL_RATIO = 1.332;

/**
 * Floor “cramped?” threshold (~2×360 rails + usable main column + scrollbar/gutter).
 */
const FT_PANEL_FULL_LAYOUT_ABS_FLOOR_PX =
	360 * 2 + 720 + 96 + 24;

/** Cap ratio-derived threshold (keeps ultra-wide monitors “desktop” longer at moderate zoom than 1080-class). */
const FT_PANEL_FULL_LAYOUT_ABS_CEIL_PX = Math.ceil(
	1536 / FT_PANEL_ZOOM_LAYOUT_TO_NOMINAL_RATIO
);

function ftPanelChromeCrampedLayoutThresholdPx(nominal: unknown): number {
	let n =
		typeof nominal === 'number' && nominal > 0
			? nominal
			: 0;
	if (n <= 0) return FT_PANEL_FULL_LAYOUT_ABS_FLOOR_PX;
	const fromRatio = Math.floor(n / FT_PANEL_ZOOM_LAYOUT_TO_NOMINAL_RATIO);
	const capped = Math.min(fromRatio, FT_PANEL_FULL_LAYOUT_ABS_CEIL_PX);
	return Math.max(FT_PANEL_FULL_LAYOUT_ABS_FLOOR_PX, capped);
}

/** Layout viewport CSS width (`innerWidth`); shrinks under desktop page zoom — used for cramped-rail thresholds only. */
function ftPanelChromeLayoutViewportCssWidthPx(): number {
	if (typeof window === 'undefined') return 0;
	return typeof window.innerWidth === 'number' ? Math.max(0, window.innerWidth) : 0;
}

/**
 * ~Nominal viewport width ignoring page zoom (“what monitor/window breakpoint band is this”).
 * When scale is unavailable, falls back to layout width alone.
 */
function ftPanelChromeApproxNominalCssWidthPx(): number {
	const layoutW = ftPanelChromeLayoutViewportCssWidthPx();
	const vv = typeof window !== 'undefined' ? window.visualViewport : null;
	const rawS = vv && typeof vv.scale === 'number' && vv.scale > 0 ? vv.scale : 1;
	const s =
		rawS < 0.05 || rawS > 8
			? 1
			: rawS;
	let approx = layoutW * s;
	const maxNominal = typeof screen !== 'undefined' && typeof screen.width === 'number'
		? Math.max(screen.width, 8192)
		: 8192;
	if (!Number.isFinite(approx)) {
		return layoutW;
	}
	if (approx < layoutW || approx > maxNominal) {
		return layoutW;
	}
	return approx;
}

/** True when viewport cannot unobstructedly host full desktop side-rail behaviour (tabs instead). */
function isNarrowViewport(): boolean {
	if (typeof window === 'undefined') return false;
	const layoutW = ftPanelChromeLayoutViewportCssWidthPx();
	const nominal = ftPanelChromeApproxNominalCssWidthPx();
	if (nominal > 0 && nominal < FT_PANEL_CHROME_MEDIA_MIN_XXL_PX) {
		return true;
	}
	if (nominal <= 0) {
		return layoutW > 0 && layoutW < FT_PANEL_CHROME_MEDIA_MIN_XXL_PX;
	}
	const crampedUnder = ftPanelChromeCrampedLayoutThresholdPx(nominal);
	return layoutW < crampedUnder;
}

/**
 * Edge tabs + html.ft-mobile-panel-chrome (overscroll) — CSS alone cannot infer cramped xxl+ windows or zoom.
 *
 * Per-tab visibility:
 *   - Narrow viewport: Info + Log tabs always visible (drawers slide over them; dwell-to-close still works).
 *   - Desktop:         Info + Log tabs visible iff the partner drawer is closed — so an open drawer
 *                      never has a tab strip eating clickable surface on its inner edge.
 *
 * Also toggles `html.ft-sidelog-open` / `html.ft-sideinfo-open` so CSS can shrink the
 * XZ full-screen container's matching edge (and shift the encounter torch off sidelog).
 */
function syncFtMobilePanelChrome(): void {
	if (typeof document === 'undefined' || !document.documentElement) return;
	const narrow = typeof isNarrowViewport === 'function' && isNarrowViewport();
	document.documentElement.classList.toggle('ft-mobile-panel-chrome', narrow);
	const sidelogOpen = typeof isSideLogOpen === 'function' && isSideLogOpen();
	const sideinfoOpen = typeof isSideInfoPanelOpen === 'function' && isSideInfoPanelOpen();
	document.documentElement.classList.toggle('ft-sidelog-open', sidelogOpen);
	document.documentElement.classList.toggle('ft-sideinfo-open', sideinfoOpen);
	const hideInfoTab = !narrow && sideinfoOpen;
	const hideLogTab = !narrow && sidelogOpen;
	const infoEl = document.getElementById('ft_mobile_panel_tab_info');
	if (infoEl) infoEl.classList.toggle('d-none', hideInfoTab);
	const logEl = document.getElementById('ft_mobile_panel_tab_log');
	if (logEl) logEl.classList.toggle('d-none', hideLogTab);
	if (typeof window.ftSyncEncMobileTab === 'function') {
		window.ftSyncEncMobileTab();
	}
}

/**
 * When true, openItemInfo() should only replace #div_refresh_side_info HTML — do not
 * forceOpenSideInfoPanel() — so log/forms stay on top on phone / iPad (matches isNarrowViewport).
 */
function __ftShouldQuietRefreshSideInfoPanel(): boolean {
	return typeof isNarrowViewport === 'function' && isNarrowViewport();
}

function getMobilePanelTab(which: unknown): HTMLElement | null {
	return document.getElementById(
		which === 'info' ? 'ft_mobile_panel_tab_info' : 'ft_mobile_panel_tab_log'
	);
}

/** Flash the tab to tell the user there's new content without hijacking the screen. */
function signalMobilePanelTabAttention(which: unknown): void {
	const tab = getMobilePanelTab(which);
	if (!tab) return;
	tab.classList.add('is-attention');
}

function clearMobilePanelTabAttention(which: unknown): void {
	const tab = getMobilePanelTab(which);
	if (!tab) return;
	tab.classList.remove('is-attention');
}

function setMobilePanelTabOpenState(which: unknown, isOpen: unknown): void {
	const tab = getMobilePanelTab(which);
	if (tab) {
		tab.classList.toggle('is-open', !!isOpen);
		tab.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
		if (isOpen) tab.classList.remove('is-attention');
		if (which === 'log') {
			syncMobileLogTabSidelogWarningHighlight();
		}
	}
	// Every open/close path funnels through here — refresh desktop visibility + ft-sidelog-open class.
	if (typeof syncFtMobilePanelChrome === 'function') {
		syncFtMobilePanelChrome();
	}
}

// ===========================================================================================
// Window attachment — every original top-level fn was a window global; consumers span the
// encounter-map bundle, side-panels-init.js, encounter-overlays, pickpocket + template.js (all
// runtime). The FT_PANEL_* consts are ALSO window-attached: template.js's DOMContentLoaded-time
// `bindMobilePanelTabs` IIFE references FT_PANEL_CHROME_MEDIA_MIN_XXL_PX as a bare global (regression
// fix 2026-06-14 — was originally kept module-local, which broke that bare reference). Attaching them
// restores the global binding the bare ref resolves to (DOMContentLoaded runs after this bundle loads).
// ===========================================================================================
declare global {
    interface Window {
        FT_PANEL_CHROME_MEDIA_MIN_XXL_PX: typeof FT_PANEL_CHROME_MEDIA_MIN_XXL_PX;
        FT_PANEL_ZOOM_LAYOUT_TO_NOMINAL_RATIO: typeof FT_PANEL_ZOOM_LAYOUT_TO_NOMINAL_RATIO;
        FT_PANEL_FULL_LAYOUT_ABS_FLOOR_PX: typeof FT_PANEL_FULL_LAYOUT_ABS_FLOOR_PX;
        FT_PANEL_FULL_LAYOUT_ABS_CEIL_PX: typeof FT_PANEL_FULL_LAYOUT_ABS_CEIL_PX;
        ftPanelChromeCrampedLayoutThresholdPx: typeof ftPanelChromeCrampedLayoutThresholdPx;
        ftPanelChromeLayoutViewportCssWidthPx: typeof ftPanelChromeLayoutViewportCssWidthPx;
        ftPanelChromeApproxNominalCssWidthPx: typeof ftPanelChromeApproxNominalCssWidthPx;
        isNarrowViewport: typeof isNarrowViewport;
        syncFtMobilePanelChrome: typeof syncFtMobilePanelChrome;
        __ftShouldQuietRefreshSideInfoPanel: typeof __ftShouldQuietRefreshSideInfoPanel;
        getMobilePanelTab: typeof getMobilePanelTab;
        signalMobilePanelTabAttention: typeof signalMobilePanelTabAttention;
        clearMobilePanelTabAttention: typeof clearMobilePanelTabAttention;
        setMobilePanelTabOpenState: typeof setMobilePanelTabOpenState;
        ftSyncEncMobileTab?: () => void;
    }
}

Object.assign(window, {
    FT_PANEL_CHROME_MEDIA_MIN_XXL_PX,
    FT_PANEL_ZOOM_LAYOUT_TO_NOMINAL_RATIO,
    FT_PANEL_FULL_LAYOUT_ABS_FLOOR_PX,
    FT_PANEL_FULL_LAYOUT_ABS_CEIL_PX,
    ftPanelChromeCrampedLayoutThresholdPx,
    ftPanelChromeLayoutViewportCssWidthPx,
    ftPanelChromeApproxNominalCssWidthPx,
    isNarrowViewport,
    syncFtMobilePanelChrome,
    __ftShouldQuietRefreshSideInfoPanel,
    getMobilePanelTab,
    signalMobilePanelTabAttention,
    clearMobilePanelTabAttention,
    setMobilePanelTabOpenState,
});

export {};
